// filepath: apps/web/src/features/lists/tree/hooks/useTreeExpansion.ts
//
// FR-UI-VIEW-2 AC-2 / AC-3 — local expand/collapse state for the
// Tree view.
//
// The Tree renderer must support client-side toggle without
// issuing a server refetch (FR-UI-VIEW-2 AC-3 — "expanding it
// reveals them without a new server fetch"). The expand/collapse
// state lives in this hook so the same expansion preferences
// survive across renders and so the test harness can exercise
// the toggle semantics in isolation (the React component tests
// can swap a custom hook provider, or render the hook directly
// via `@testing-library/react renderHook`).
//
// State shape:
//   - `expanded: Set<string>` — the set of record ids whose
//     subtree is currently rendered. Root records can be either
//     expanded (children visible) or collapsed (children hidden,
//     chevron still rendered).
//   - `toggle(id: string)` — flips membership in the set.
//   - `expand(id: string)` / `collapse(id: string)` — explicit
//     setters used by the "expand all" / "collapse all" surfaces.
//
// Initial state: empty Set (no records expanded by default).
// The TreeView seeds the initial state with the root ids on
// mount so root nodes start expanded — see TreeView for the
// seeding pass.

import { useCallback, useState } from "react";

export interface UseTreeExpansionResult {
  /** The set of record ids whose subtree is currently rendered. */
  expanded: ReadonlySet<string>;
  /** Flip membership of one id. Idempotent. */
  toggle: (id: string) => void;
  /** Force-expand a single id. Idempotent. */
  expand: (id: string) => void;
  /** Force-collapse a single id. Idempotent. */
  collapse: (id: string) => void;
  /** Replace the entire expanded set (used by "expand all" / "collapse all"). */
  setAll: (ids: ReadonlyArray<string>) => void;
}

/**
 * useTreeExpansion — local expand/collapse state hook.
 *
 * Returns `{ expanded, toggle, expand, collapse, setAll }`. The
 * `expanded` set is a plain `Set` held in React state; toggling
 * a membership copies to a new Set so React's identity check
 * triggers a re-render without the consumer having to think
 * about reference equality.
 *
 * Optional `initial` parameter seeds the set on the first render
 * — used by TreeView to start with all roots expanded.
 */
export function useTreeExpansion(
  initial?: ReadonlyArray<string>,
): UseTreeExpansionResult {
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(initial ?? []),
  );

  const toggle = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const expand = useCallback((id: string) => {
    setExpanded((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const collapse = useCallback((id: string) => {
    setExpanded((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const setAll = useCallback((ids: ReadonlyArray<string>) => {
    setExpanded(() => new Set(ids));
  }, []);

  return { expanded, toggle, expand, collapse, setAll };
}
